import type { RootState } from "./rootReducer";
import {
  ensurePrimaryList,
  selectAllShoppingLists,
  selectPrimaryListId,
} from "./slices/shoppingListsSlice";
import { assignOrphanItemsToList } from "./slices/shoppingListSlice";
import { mergeShoppingListsAction } from "./actions";

// Guarantee the user has a primary shopping list and that every item is attached
// to one (legacy rows added before lists existed get backfilled to the primary).
// Idempotent — safe to call whenever auth resolves / re-resolves.
//
// Pass the signed-in user's id: the primary list is then created WITH that id, so
// two offline devices for the same account mint the SAME primary row (sync dedupes
// on the shared primary key) instead of two duplicates. Local-only/anonymous use
// (no userId) falls back to a random id — there's no multi-device concern then.
// Because of this, call it once auth has resolved (not merely after rehydration,
// when the user id may still be unknown). Returning users already have a persisted
// primary, so nothing is created for them.
export function ensureShoppingListsReady(
  store: { getState: () => RootState; dispatch: (action: unknown) => void },
  userId?: string,
): void {
  if (selectAllShoppingLists(store.getState()).length === 0) {
    store.dispatch(ensurePrimaryList(userId ? { id: userId } : {}));
  }

  // Reconcile duplicate primaries. A pre-deterministic-id install (or two offline
  // devices) could each mint their own primary → several after sync, none
  // deletable. Deterministically pick ONE canonical (the user-id primary if
  // present, else the smallest id — so every device agrees) and merge the rest
  // into it: their items re-parent to the canonical, the duplicates are removed.
  const primaries = selectAllShoppingLists(store.getState()).filter((l) => l.isPrimary);
  if (primaries.length > 1) {
    const canonical =
      primaries.find((l) => l.id === userId) ??
      [...primaries].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))[0];
    const fromIds = primaries.filter((l) => l.id !== canonical.id).map((l) => l.id);
    store.dispatch(mergeShoppingListsAction({ fromIds, intoId: canonical.id }));
  }

  const primaryId = selectPrimaryListId(store.getState());
  if (primaryId) {
    store.dispatch(assignOrphanItemsToList({ listId: primaryId }));
  }
}
